import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Typography, Alert, Space } from 'antd';
import { CopyOutlined, CheckOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { createInvoice } from '../firebase/firestore';
import { isTelegramMiniApp, shareViaTelegram } from '../telegram/webApp';
import '../pages/CreateInvoice.css';
import InvoiceOfferFields from './InvoiceOfferFields';

const { Text } = Typography;
const { TextArea } = Input;

const telegramBotUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME?.replace(/^@/, '');
const integrationTutorialUrl = telegramBotUsername
  ? `https://t.me/${telegramBotUsername}?start=integrate`
  : null;

type InputMode = 'STARS' | 'EUR' | 'USD';

export interface CreateInvoiceFormProps {
  /** Called when the user cancels (form view) */
  onCancel: () => void;
  /** Called after an invoice has been successfully created */
  onCreated?: (invoiceLink: string) => void;
  /** Called when the user clicks "View invoices" on the success view */
  onViewInvoices?: () => void;
  /** Called whenever the form switches between form view and success view */
  onSuccessViewChange?: (showingSuccess: boolean) => void;
}

const CreateInvoiceForm: React.FC<CreateInvoiceFormProps> = ({
  onCancel,
  onCreated,
  onViewInvoices,
  onSuccessViewChange,
}) => {
  const { firebaseUser } = useAuth();
  const { t } = useI18n();
  const [form] = Form.useForm();
  const [inputMode, setInputMode] = useState<InputMode>('STARS');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoiceLink, setInvoiceLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const inMiniApp = isTelegramMiniApp();

  useEffect(() => {
    form.setFieldsValue({ inputMode: 'STARS' });
    setInputMode('STARS');
  }, [form]);

  useEffect(() => {
    onSuccessViewChange?.(!!invoiceLink);
  }, [invoiceLink, onSuccessViewChange]);

  const resetToForm = () => {
    setInvoiceLink(null);
    setError(null);
    form.resetFields();
    form.setFieldsValue({ inputMode: 'STARS' });
    setInputMode('STARS');
  };

  const handleSubmit = async (values: { amount: number; title?: string; description?: string; inputMode?: string; singleUse?: boolean; invoiceType?: any; deliveryType?: any; deliveryValue?: string; deliveryMessage?: string; subscriptionMode?: any; periodDays?: number }) => {
    if (!firebaseUser) {
      setError(t.app.common.mustBeLoggedIn);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const mode = (values.inputMode || inputMode) as InputMode;
      const numAmount = parseFloat(values.amount.toString());
      if (isNaN(numAmount) || numAmount <= 0) {
        throw new Error(t.app.createInvoice.invalidAmount);
      }

      if (mode === 'STARS' && !Number.isInteger(numAmount)) {
        throw new Error(t.app.createInvoice.starsMustBeInteger);
      }

      const data = await createInvoice({
        inputMode: mode,
        amount: numAmount,
        title: values.title || undefined,
        description: values.description || undefined,
        singleUse: values.invoiceType === 'SINGLE' || (values.invoiceType === 'CONTENT' && !!values.singleUse),
        invoiceType: values.invoiceType || 'MULTIPLE',
        delivery: ['CONTENT', 'SUBSCRIPTION'].includes(values.invoiceType) ? { type: values.deliveryType, value: values.deliveryValue!, message: values.deliveryMessage } : undefined,
        subscription: values.invoiceType === 'SUBSCRIPTION' ? { mode: values.subscriptionMode || 'AUTO', periodDays: values.subscriptionMode === 'MANUAL' ? values.periodDays! : 30 } : undefined,
      });

      setInvoiceLink(data.invoiceLink);
      onCreated?.(data.invoiceLink);
    } catch (err: any) {
      setError(err.message || t.app.createInvoice.createError);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (invoiceLink) {
      navigator.clipboard.writeText(invoiceLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (invoiceLink) {
    return (
      <div className="invoice-link-content">
        <div className="success-icon">
          <CheckOutlined />
        </div>
        <Text className="link-label">{t.app.createInvoice.paymentLink}</Text>
        <div className="link-box">
          <Input value={invoiceLink} readOnly className="link-input" />
          <Button
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={copyLink}
            className="copy-button"
            type="primary"
          >
            {copied ? t.app.common.copied : t.app.common.copy}
          </Button>
        </div>
        {integrationTutorialUrl && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 24, textAlign: 'left' }}
            message={t.app.createInvoice.integrationTitle}
            description={
              <>
                {t.app.createInvoice.integrationDesc}{' '}
                <a href={integrationTutorialUrl} target="_blank" rel="noopener noreferrer">
                  {t.app.createInvoice.openTutorial}
                </a>
              </>
            }
          />
        )}
        <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 24 }}>
          {inMiniApp && (
            <Button
              type="primary"
              size="large"
              block
              icon={<SendOutlined />}
              onClick={() => shareViaTelegram(invoiceLink, form.getFieldValue('title') || undefined)}
              className="test-link-button"
            >
              {t.app.createInvoice.shareTelegram}
            </Button>
          )}
          <Button
            type={inMiniApp ? 'default' : 'primary'}
            size="large"
            block
            href={invoiceLink}
            target="_blank"
            rel="noopener noreferrer"
            className="test-link-button"
          >
            {t.app.createInvoice.testLink}
          </Button>
          {onViewInvoices && (
            <Button size="large" block onClick={onViewInvoices} className="view-invoices-button">
              {t.app.createInvoice.viewInvoices}
            </Button>
          )}
          <Button size="large" block onClick={resetToForm} className="new-invoice-button">
            {t.app.createInvoice.createNew}
          </Button>
        </Space>
      </div>
    );
  }

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit} size="middle" className="create-invoice-form">
      <InvoiceOfferFields />
      <div className="create-invoice-sep" />
      <Form.Item label={t.app.createInvoice.paymentMode} name="inputMode" initialValue="STARS">
        <Select
          onChange={(value) => {
            const mode = value as InputMode;
            setInputMode(mode);
            form.setFieldsValue({ inputMode: mode });
          }}
          className="create-invoice-select"
        >
          <Select.Option value="STARS">{t.app.createInvoice.modeStars}</Select.Option>
          <Select.Option value="EUR">{t.app.createInvoice.modeEur}</Select.Option>
          <Select.Option value="USD">{t.app.createInvoice.modeUsd}</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={inputMode === 'STARS' ? t.app.createInvoice.amountInteger : t.app.createInvoice.amountDecimal}
        name="amount"
        rules={[
          { required: true, message: t.app.createInvoice.amountRequired },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              const currentMode = form.getFieldValue('inputMode') || inputMode;

              if (currentMode === 'STARS') {
                const intValue = parseInt(value, 10);
                if (isNaN(intValue) || intValue <= 0) {
                  return Promise.reject(new Error(t.app.createInvoice.amountIntegerError));
                }
                if (value.toString().includes('.') || value.toString().includes(',')) {
                  return Promise.reject(new Error(t.app.createInvoice.amountNoDecimals));
                }
                if (intValue !== parseFloat(value)) {
                  return Promise.reject(new Error(t.app.createInvoice.amountMustBeInteger));
                }
              } else {
                const numValue = parseFloat(value);
                if (isNaN(numValue) || numValue <= 0) {
                  return Promise.reject(new Error(t.app.createInvoice.amountGreaterThanZero));
                }
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input
          type="number"
          step={inputMode === 'STARS' ? '1' : '0.01'}
          min={inputMode === 'STARS' ? '1' : '0.01'}
          placeholder={inputMode === 'STARS' ? '1000' : '10.50'}
          className="create-invoice-input"
          onKeyDown={(e) => {
            if (inputMode === 'STARS' && (e.key === ',' || e.key === '.')) {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            if (inputMode === 'STARS') {
              const value = e.target.value;
              if (value.includes('.') || value.includes(',')) {
                const intValue = Math.floor(parseFloat(value) || 0);
                e.target.value = intValue > 0 ? intValue.toString() : '';
                form.setFieldsValue({ amount: intValue > 0 ? intValue : undefined });
              }
            }
          }}
        />
      </Form.Item>

      <Form.Item label={t.app.createInvoice.titleOptional} name="title">
        <Input placeholder={t.app.createInvoice.titlePlaceholder} className="create-invoice-input" />
      </Form.Item>

      <Form.Item label={t.app.createInvoice.descriptionOptional} name="description">
        <TextArea rows={3} placeholder={t.app.createInvoice.descriptionPlaceholder} className="create-invoice-textarea" />
      </Form.Item>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="create-invoice-alert"
        />
      )}

      <Form.Item style={{ marginBottom: 0 }}>
        <div className="create-invoice-footer">
          <Button onClick={onCancel} className="create-invoice-cancel">
            {t.app.createInvoice.cancel}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!firebaseUser}
            icon={<PlusOutlined />}
            className="create-invoice-submit"
          >
            {loading ? t.app.common.creating : t.app.createInvoice.createLink}
          </Button>
        </div>
      </Form.Item>
    </Form>
  );
};

export default CreateInvoiceForm;
